/**
 * Claim Organization Modal
 *
 * Form modal for claiming an existing organization.
 * Features:
 * - Organization search/select
 * - Contact fields: name, email, role
 * - Optional message field
 * - Form validation with error messages
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { X, MagnifyingGlass, CheckCircle, CircleNotch, WarningCircle } from '@phosphor-icons/react';
import { COLORS } from '../../lib/constants';
import { isValidEmail } from '../../lib/validation';
import { modalStyles, getCloseButtonStyle } from './styles';
import { useOrganizations } from '../../hooks';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface ValidationErrors {
  organization?: string;
  contactName?: string;
  contactEmail?: string;
  role?: string;
}

interface ClaimOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Component-specific styles extending shared modal styles
const styles = {
  ...modalStyles,
  modal: {
    ...modalStyles.modal,
    maxWidth: '560px',
  },
  searchContainer: {
    position: 'relative' as const,
  },
  searchInput: {
    width: '100%',
    padding: '10px 12px 10px 40px',
    fontSize: '14px',
    borderRadius: 0,
    border: `1px solid ${COLORS.graphite}30`,
    backgroundColor: '#fff',
    color: COLORS.graphite,
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  searchIcon: {
    position: 'absolute' as const,
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: `${COLORS.graphite}60`,
    pointerEvents: 'none' as const,
  },
  searchResults: {
    position: 'absolute' as const,
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    border: `1px solid ${COLORS.graphite}30`,
    borderRadius: 0,
    marginTop: '4px',
    maxHeight: '200px',
    overflow: 'auto',
    zIndex: 10,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  },
  searchResultItem: {
    padding: '10px 12px',
    cursor: 'pointer',
    borderBottom: `1px solid ${COLORS.graphite}10`,
    transition: 'background-color 0.15s ease',
  },
  searchResultItemHover: {
    backgroundColor: `${COLORS.royalBlue}10`,
  },
  searchResultName: {
    fontSize: '14px',
    color: COLORS.graphite,
  },
  searchResultCity: {
    fontSize: '12px',
    color: `${COLORS.graphite}80`,
    marginTop: '2px',
  },
  selectedOrg: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px',
    backgroundColor: `${COLORS.royalBlue}10`,
    borderRadius: 0,
    border: `1px solid ${COLORS.royalBlue}30`,
  },
  selectedOrgName: {
    fontSize: '14px',
    color: COLORS.graphite,
  },
  selectedOrgCity: {
    fontSize: '12px',
    color: `${COLORS.graphite}80`,
    marginTop: '2px',
  },
  changeButton: {
    fontSize: '13px',
    color: COLORS.royalBlue,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: '4px 8px',
  },
  noResults: {
    padding: '12px',
    textAlign: 'center' as const,
    color: `${COLORS.graphite}60`,
    fontSize: '14px',
  },
};

interface Organization {
  id: string;
  name: string;
  city?: string;
}

export function ClaimOrganizationModal({
  isOpen,
  onClose,
}: ClaimOrganizationModalProps) {
  const { organizations } = useOrganizations();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [closeHovered, setCloseHovered] = useState(false);

  // Form fields
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [role, setRole] = useState('');
  const [message, setMessage] = useState('');

  // Validation state
  const [errors, setErrors] = useState<ValidationErrors>({});

  // Submission state
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [submitError, setSubmitError] = useState<string>('');
  // Ref to prevent double-click submissions (synchronous check before async state update)
  const isSubmittingRef = useRef(false);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Reset state and close
        setSearchQuery('');
        setSelectedOrg(null);
        setContactName('');
        setContactEmail('');
        setRole('');
        setMessage('');
        setErrors({});
        setSubmitStatus('idle');
        setSubmitError('');
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Filter organizations based on search query
  const filteredOrgs = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return organizations
      .filter((org) =>
        org.name.toLowerCase().includes(query) ||
        (org.city && org.city.toLowerCase().includes(query))
      )
      .slice(0, 10); // Limit to 10 results
  }, [organizations, searchQuery]);

  // Validation function
  const validateForm = (): ValidationErrors => {
    const newErrors: ValidationErrors = {};

    if (!selectedOrg) {
      newErrors.organization = 'Selecteer een organisatie';
    }

    if (!contactName.trim()) {
      newErrors.contactName = 'Uw naam is verplicht';
    }

    if (!contactEmail.trim()) {
      newErrors.contactEmail = 'Uw e-mail is verplicht';
    } else if (!isValidEmail(contactEmail)) {
      newErrors.contactEmail = 'Voer een geldig e-mailadres in';
    }

    if (!role.trim()) {
      newErrors.role = 'Uw functie is verplicht';
    }

    return newErrors;
  };

  // Clear error when field is updated
  const handleContactNameChange = (value: string) => {
    setContactName(value);
    if (errors.contactName) {
      setErrors(prev => ({ ...prev, contactName: undefined }));
    }
  };

  const handleContactEmailChange = (value: string) => {
    setContactEmail(value);
    if (errors.contactEmail) {
      setErrors(prev => ({ ...prev, contactEmail: undefined }));
    }
  };

  const handleRoleChange = (value: string) => {
    setRole(value);
    if (errors.role) {
      setErrors(prev => ({ ...prev, role: undefined }));
    }
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSelectOrg = (org: Organization) => {
    setSelectedOrg(org);
    setSearchQuery('');
    setShowResults(false);
    // Clear organization error when selected
    if (errors.organization) {
      setErrors(prev => ({ ...prev, organization: undefined }));
    }
  };

  const handleChangeOrg = () => {
    setSelectedOrg(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent double-click submissions using synchronous ref check
    // This prevents race conditions where multiple clicks happen before state updates
    if (isSubmittingRef.current) {
      return;
    }

    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      // Scroll to first error
      const firstErrorField = Object.keys(validationErrors)[0];
      const errorElement = document.querySelector(`[data-field="${firstErrorField}"]`);
      errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Mark as submitting immediately (synchronous) to prevent double-clicks
    isSubmittingRef.current = true;

    // Submit to API
    setSubmitStatus('submitting');
    setSubmitError('');

    try {
      const response = await fetch(`${API_BASE_URL}/claim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          organizationId: selectedOrg?.id,
          organizationName: selectedOrg?.name,
          contactName,
          contactEmail,
          role,
          message: message || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Er is een fout opgetreden');
      }

      setSubmitStatus('success');
      // Reset ref on success (form will show success view)
      isSubmittingRef.current = false;
    } catch (error) {
      console.error('Claim submit error:', error);
      setSubmitStatus('error');
      // Always show user-friendly message, never expose technical details
      setSubmitError('Er is een fout opgetreden bij het claimen. Probeer het later opnieuw.');
      // Reset ref on error so user can retry
      isSubmittingRef.current = false;
    }
  };

  const resetForm = () => {
    setSearchQuery('');
    setSelectedOrg(null);
    setContactName('');
    setContactEmail('');
    setRole('');
    setMessage('');
    setErrors({});
    setSubmitStatus('idle');
    setSubmitError('');
    // Reset double-click prevention ref
    isSubmittingRef.current = false;
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Success view
  if (submitStatus === 'success') {
    return (
      <div style={styles.overlay} onClick={handleOverlayClick}>
        <div style={styles.modal} role="dialog" aria-modal="true" aria-labelledby="claim-modal-title">
          <div style={styles.header}>
            <h2 id="claim-modal-title" style={styles.title}>Organisatie claimen</h2>
            <button
              style={getCloseButtonStyle(closeHovered)}
              onClick={handleClose}
              aria-label="Sluiten"
              onMouseEnter={() => setCloseHovered(true)}
              onMouseLeave={() => setCloseHovered(false)}
            >
              <X size={20} weight="bold" />
            </button>
          </div>
          <div style={styles.statusContainer}>
            <CheckCircle size={64} weight="fill" color="#22c55e" style={styles.statusIcon} />
            <h3 style={styles.statusTitle}>Bedankt voor uw aanvraag!</h3>
            <p style={styles.statusMessage}>
              Uw aanvraag om {selectedOrg?.name} te claimen is succesvol verstuurd.
              We nemen zo snel mogelijk contact met u op.
            </p>
            <button style={styles.statusButton} onClick={handleClose}>
              Sluiten
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Error view
  if (submitStatus === 'error') {
    return (
      <div style={styles.overlay} onClick={handleOverlayClick}>
        <div style={styles.modal} role="dialog" aria-modal="true" aria-labelledby="claim-modal-title">
          <div style={styles.header}>
            <h2 id="claim-modal-title" style={styles.title}>Organisatie claimen</h2>
            <button
              style={getCloseButtonStyle(closeHovered)}
              onClick={handleClose}
              aria-label="Sluiten"
              onMouseEnter={() => setCloseHovered(true)}
              onMouseLeave={() => setCloseHovered(false)}
            >
              <X size={20} weight="bold" />
            </button>
          </div>
          <div style={styles.statusContainer}>
            <WarningCircle size={64} weight="fill" color="#dc2626" style={styles.statusIcon} />
            <h3 style={styles.statusTitle}>Er is iets misgegaan</h3>
            <p style={styles.statusMessage}>
              {submitError || 'Er is een fout opgetreden bij het claimen. Probeer het later opnieuw.'}
            </p>
            <button style={styles.statusButton} onClick={() => setSubmitStatus('idle')}>
              Opnieuw proberen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.modal} role="dialog" aria-modal="true" aria-labelledby="claim-modal-title">
        <div style={styles.header}>
          <h2 id="claim-modal-title" style={styles.title}>Organisatie claimen</h2>
          <button
            style={{
              ...styles.closeButton,
              backgroundColor: closeHovered ? `${COLORS.graphite}10` : 'transparent',
            }}
            onClick={handleClose}
            aria-label="Sluiten"
            onMouseEnter={() => setCloseHovered(true)}
            onMouseLeave={() => setCloseHovered(false)}
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <div style={styles.content}>
          <form style={styles.form} onSubmit={handleSubmit}>
            {/* Organization Search */}
            <div style={styles.fieldGroup} data-field="organization">
              <label htmlFor="claim-org-search" style={styles.label}>
                Organisatie<span style={styles.required}>*</span>
              </label>
              {selectedOrg ? (
                <div style={styles.selectedOrg}>
                  <div>
                    <div style={styles.selectedOrgName}>{selectedOrg.name}</div>
                    {selectedOrg.city && (
                      <div style={styles.selectedOrgCity}>{selectedOrg.city}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    style={styles.changeButton}
                    onClick={handleChangeOrg}
                  >
                    Wijzigen
                  </button>
                </div>
              ) : (
                <div style={styles.searchContainer}>
                  <MagnifyingGlass size={18} style={styles.searchIcon} />
                  <input
                    id="claim-org-search"
                    type="text"
                    style={{
                      ...styles.searchInput,
                      ...(errors.organization ? styles.inputError : {}),
                    }}
                    placeholder="Zoek organisatie..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setShowResults(true)}
                    onBlur={() => {
                      // Delay hiding to allow click on results
                      setTimeout(() => setShowResults(false), 200);
                    }}
                    aria-invalid={!!errors.organization}
                    aria-describedby={errors.organization ? 'organization-error' : undefined}
                  />
                  {showResults && searchQuery.trim() && (
                    <div style={styles.searchResults}>
                      {filteredOrgs.length > 0 ? (
                        filteredOrgs.map((org, index) => (
                          <div
                            key={org.id}
                            style={{
                              ...styles.searchResultItem,
                              ...(hoveredIndex === index ? styles.searchResultItemHover : {}),
                            }}
                            onClick={() => handleSelectOrg(org)}
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => setHoveredIndex(-1)}
                          >
                            <div style={styles.searchResultName}>{org.name}</div>
                            {org.city && (
                              <div style={styles.searchResultCity}>{org.city}</div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div style={styles.noResults}>
                          Geen organisaties gevonden
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {errors.organization && (
                <span id="organization-error" style={styles.errorText} role="alert">
                  {errors.organization}
                </span>
              )}
            </div>

            {/* Contact Name */}
            <div style={styles.fieldGroup} data-field="contactName">
              <label htmlFor="claim-org-contactName" style={styles.label}>
                Uw naam<span style={styles.required}>*</span>
              </label>
              <input
                id="claim-org-contactName"
                type="text"
                style={{
                  ...styles.input,
                  ...(errors.contactName ? styles.inputError : {}),
                }}
                placeholder="Uw volledige naam"
                value={contactName}
                onChange={(e) => handleContactNameChange(e.target.value)}
                aria-invalid={!!errors.contactName}
                aria-describedby={errors.contactName ? 'contactName-error' : undefined}
              />
              {errors.contactName && (
                <span id="contactName-error" style={styles.errorText} role="alert">
                  {errors.contactName}
                </span>
              )}
            </div>

            {/* Contact Email */}
            <div style={styles.fieldGroup} data-field="contactEmail">
              <label htmlFor="claim-org-contactEmail" style={styles.label}>
                Uw e-mail<span style={styles.required}>*</span>
              </label>
              <input
                id="claim-org-contactEmail"
                type="email"
                style={{
                  ...styles.input,
                  ...(errors.contactEmail ? styles.inputError : {}),
                }}
                placeholder="uw@email.nl"
                value={contactEmail}
                onChange={(e) => handleContactEmailChange(e.target.value)}
                aria-invalid={!!errors.contactEmail}
                aria-describedby={errors.contactEmail ? 'contactEmail-error' : undefined}
              />
              {errors.contactEmail && (
                <span id="contactEmail-error" style={styles.errorText} role="alert">
                  {errors.contactEmail}
                </span>
              )}
            </div>

            {/* Role */}
            <div style={styles.fieldGroup} data-field="role">
              <label htmlFor="claim-org-role" style={styles.label}>
                Uw functie bij de organisatie<span style={styles.required}>*</span>
              </label>
              <input
                id="claim-org-role"
                type="text"
                style={{
                  ...styles.input,
                  ...(errors.role ? styles.inputError : {}),
                }}
                placeholder="Bijv. Directeur, Eigenaar, Manager"
                value={role}
                onChange={(e) => handleRoleChange(e.target.value)}
                aria-invalid={!!errors.role}
                aria-describedby={errors.role ? 'role-error' : undefined}
              />
              {errors.role && (
                <span id="role-error" style={styles.errorText} role="alert">
                  {errors.role}
                </span>
              )}
            </div>

            {/* Message */}
            <div style={styles.fieldGroup}>
              <label htmlFor="claim-org-message" style={styles.label}>Bericht (optioneel)</label>
              <textarea
                id="claim-org-message"
                style={styles.textarea}
                placeholder="Heeft u nog iets toe te voegen?"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
          </form>
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelButton} onClick={handleClose} disabled={submitStatus === 'submitting'}>
            Annuleren
          </button>
          <button
            type="submit"
            style={{
              ...styles.submitButton,
              ...(submitStatus === 'submitting' ? styles.submitButtonDisabled : {}),
            }}
            onClick={handleSubmit}
            disabled={submitStatus === 'submitting'}
          >
            {submitStatus === 'submitting' ? (
              <>
                <CircleNotch size={16} weight="bold" style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }} />
                Bezig met versturen...
              </>
            ) : (
              'Versturen'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

